import { useState, Fragment } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Landmark,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Ban,
  ArrowRightLeft,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Copy,
} from "lucide-react";

// Helper functions
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const s = String(dateStr).trim();
  if (!s || s === "-") return "-";
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);          // YYYY-MM-DD / ISO
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  m = s.match(/^(\d{4})-(\d{1,2})$/);                    // YYYY-MM (competencia)
  if (m) return `${m[2].padStart(2, "0")}/${m[1]}`;
  m = s.match(/^(\d{1,2})\/(\d{4})$/);                   // MM/YYYY (competencia)
  if (m) return `${m[1].padStart(2, "0")}/${m[2]}`;
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);           // DD/MM/YYYY
  if (m) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
  return s;
};

// ===== Helpers de cálculo REFIN/PORTABILIDADE (parâmetros dinâmicos do banco) =====
const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const n = parseFloat(String(val).replace(/[^\d.,-]/g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};

const instMatch = (a, b) => {
  if (!a || !b) return false;
  const ca = String(a).split(" - ")[0].trim();
  const cb = String(b).split(" - ")[0].trim();
  return String(a).trim() === String(b).trim() || (ca && ca === cb);
};

const findBancoDoContrato = (bancos, contrato) => {
  if (!bancos || !bancos.length) return null;
  const cod = contrato.banco_codigo || (contrato.banco_nome || "").split(" - ")[0];
  const nome = contrato.banco_nome || "";
  return (
    bancos.find(
      (b) =>
        b.codigo === cod ||
        (b.nome && nome && (b.nome.includes(nome) || nome.includes(b.nome)))
    ) || null
  );
};

const findParametroAtivo = (banco, tipoOperacao, instituicao) => {
  if (!banco || !banco.parametros) return null;
  return (
    banco.parametros.find(
      (p) =>
        p.ativo !== false &&
        p.tipoOperacao === tipoOperacao &&
        (instMatch(p.instituicao, instituicao) || !p.instituicao)
    ) || null
  );
};

// Índice de desconto por faixa de PARCELAS PAGAS
const getIndice = (param, pagas) => {
  if (pagas <= 30) return parseNum(param.indiceSaldo1a30);
  if (pagas <= 50) return parseNum(param.indiceSaldo31a50);
  return parseNum(param.indiceSaldo51a100);
};

// Idade em anos a partir de data (ISO ou similar)
export const idadeAnos = (dataNasc) => {
  if (!dataNasc) return null;
  const d = new Date(dataNasc);
  if (isNaN(d.getTime())) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - d.getFullYear();
  const m = hoje.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < d.getDate())) idade--;
  return idade;
};

// Faixa etária do parâmetro que comporta a idade do cliente
const getFaixaEtaria = (param, idade) => {
  if (idade == null || !param?.faixasEtarias) return null;
  return (
    param.faixasEtarias.find((f) => {
      const ini = parseNum(f.idadeInicial);
      const fim = parseNum(f.idadeFinal);
      return (ini === null || idade >= ini) && (fim === null || idade <= fim);
    }) || null
  );
};

// Filtra regras pelo prazo permitido pela idade e mantém só o MAIOR prazo por produto
// Portabilidade: uma linha por banco (a de maior valor liberado), respeitando prazo
const agruparPorBanco = (regras, prazoMaximo) => {
  let filt = regras;
  if (prazoMaximo != null) filt = filt.filter((r) => (parseNum(r.prazo) ?? 0) <= prazoMaximo);
  const porBanco = {};
  filt.forEach((r) => {
    const atual = porBanco[r.bancoId];
    if (!atual || r.valorTroco > atual.valorTroco) porBanco[r.bancoId] = r;
  });
  return Object.values(porBanco);
};

const filtrarRegrasPorPrazo = (regras, prazoMaximo) => {
  let filt = regras;
  if (prazoMaximo != null) filt = filt.filter((r) => (parseNum(r.prazo) ?? 0) <= prazoMaximo);
  const porProduto = {};
  filt.forEach((r) => {
    const pr = parseNum(r.prazo) ?? 0;
    const atual = porProduto[r.produto];
    if (!atual || pr > (parseNum(atual.prazo) ?? 0)) porProduto[r.produto] = r;
  });
  return Object.values(porProduto);
};

// Saldo devedor (VTC - desconto pelo índice) — usado no header do card e na tabela
const calcularSaldoDevedorRefinPort = (contrato, bancos, instituicao, tipoOperacao) => {
  const banco = findBancoDoContrato(bancos, contrato);
  const param = banco ? findParametroAtivo(banco, tipoOperacao, instituicao) : null;
  if (!param) return null;
  const valorParcela = parseNum(contrato.valor_parcela) || 0;
  const parcelasRestantes = parseInt(contrato.parcelas_restantes) || 0;
  const parcelasPagas = parseInt(contrato.parcela_atual) || 0;
  const vtc = valorParcela * parcelasRestantes;
  const indice = getIndice(param, parcelasPagas) || 0;
  return vtc - vtc * (indice / 100);
};

// Projeta a janela de oportunidade: quando o cliente fica elegivel (troco >= minimo)
const projetarJanela = (valorBruto, contrato, param, trocoMin) => {
  if (!param) return null;
  const parcela = parseNum(contrato.valor_parcela) || 0;
  const pagas0 = parseInt(contrato.parcela_atual) || 0;
  const rest0 = parseInt(contrato.parcelas_restantes) || 0;
  const minTroco = Math.max(parseNum(trocoMin) ?? 0, 0.01);
  for (let m = 0; m <= rest0; m++) {
    const pagas = pagas0 + m;
    const rest = rest0 - m;
    const vtc = parcela * rest;
    const idx = (pagas <= 30 ? parseNum(param.indiceSaldo1a30) : pagas <= 50 ? parseNum(param.indiceSaldo31a50) : parseNum(param.indiceSaldo51a100)) || 0;
    const saldo = vtc - vtc * (idx / 100);
    const troco = valorBruto - saldo;
    if (troco >= minTroco) {
      const d = new Date();
      d.setMonth(d.getMonth() + m);
      return { meses: m, data: d, valor: troco, agora: m === 0 };
    }
  }
  return null;
};

// Lista as regras (tabelas de comissão) cadastradas p/ banco + instituição + operação
const getRegrasComissao = (tabelasComissao, bancos, bancoContrato, instituicao, tipoOperacao, valorParcela, saldoDevedor) => {
  if (!tabelasComissao) return [];
  const isPort = tipoOperacao === "PORTABILIDADE";
  if (!isPort && !bancoContrato) return [];
  const bancoMap = {};
  const bancoObjMap = {};
  (bancos || []).forEach((b) => { bancoMap[b.id] = b.nome; bancoObjMap[b.id] = b; });
  const tabs = tabelasComissao.filter(
    (t) =>
      (isPort ? (!bancoContrato || t.banco_id !== bancoContrato.id) : t.banco_id === bancoContrato.id) &&
      t.tipo_operacao === tipoOperacao &&
      instMatch(t.instituicao, instituicao) &&
      (t.status ? String(t.status).toLowerCase() === "ativo" : true)
  );
  const regras = [];
  tabs.forEach((tab) =>
    (tab.comissoes || []).forEach((c) => {
      const fator = parseNum(c.fator);
      if (!fator || fator <= 0) return;
      const valorLiberado = valorParcela / fator;
      const vi = parseNum(c.valorInicial);
      const vf = parseNum(c.valorFinal);
      const dentroFaixa = (vi === null || valorLiberado >= vi) && (vf === null || valorLiberado <= vf);
      if (!dentroFaixa) return;
      const empresaPct = parseNum(c.empresa) || 0;
      const atendentePct = parseNum(c.atendente) || 0;
      const valorTroco = valorLiberado - saldoDevedor;
      regras.push({
        bancoId: tab.banco_id,
        bancoNome: bancoMap[tab.banco_id] || tab.nome || "-",
        bancoParam: isPort ? findParametroAtivo(bancoObjMap[tab.banco_id], tipoOperacao, instituicao) : null,
        produto: c.produto || tab.nome || "-",
        prazo: c.prazo,
        fator,
        taxa: c.taxa,
        empresaPct,
        atendentePct,
        valorLiberado,
        valorTroco,
        comissao: valorTroco * (empresaPct / 100),
        comissaoAtendente: valorTroco * (empresaPct / 100) * (atendentePct / 100),
      });
    })
  );
  return regras;
};

// Avalia status (APROVADO/REPROVADO) de uma regra contra todos os critérios do parâmetro
const avaliarStatus = (param, contrato, valorParcela, saldoDevedor, regra, idadeCliente) => {
  if (!param) {
    const tp = regra.valorTroco > 0;
    return { aprovado: tp, criterios: [], trocoPositivo: tp };
  }
  const criterios = [];
  const pagas = parseInt(contrato.parcela_atual) || 0;

  if (idadeCliente != null && param.faixasEtarias && param.faixasEtarias.length > 0) {
    const _ini = param.faixasEtarias.map((f) => parseNum(f.idadeInicial)).filter((v) => v !== null);
    const _fim = param.faixasEtarias.map((f) => parseNum(f.idadeFinal)).filter((v) => v !== null);
    const _min = _ini.length ? Math.min(..._ini) : 0;
    const _max = _fim.length ? Math.max(..._fim) : 999;
    const _ok = param.faixasEtarias.some((f) => { const i = parseNum(f.idadeInicial); const fm = parseNum(f.idadeFinal); return (i === null || idadeCliente >= i) && (fm === null || idadeCliente <= fm); });
    criterios.push({ nome: "Idade", ok: _ok, p: `${_min}-${_max}`, c: `${idadeCliente}` });
  }

  const pPagas = parseNum(param.parcelasPagas);
  if (pPagas !== null) criterios.push({ nome: "Parc. Pagas", ok: pagas >= pPagas, p: `${pPagas}`, c: `${pagas}` });

  const pMin = parseNum(param.valorParcelaMinima);
  if (pMin !== null) criterios.push({ nome: "Parcela Mín.", ok: valorParcela >= pMin, p: formatCurrency(pMin), c: formatCurrency(valorParcela) });

  const sMin = parseNum(param.saldoDevedorMinimo);
  if (sMin !== null) criterios.push({ nome: "Saldo Dev. Mín.", ok: saldoDevedor >= sMin, p: formatCurrency(sMin), c: formatCurrency(saldoDevedor) });

  const tMin = parseNum(param.valorTroco);
  if (tMin !== null) criterios.push({ nome: "Valor Liberado", ok: regra.valorTroco >= tMin, p: formatCurrency(tMin), c: formatCurrency(regra.valorTroco) });

  const vcMin = parseNum(param.valorContratoMinimo);
  if (vcMin !== null) criterios.push({ nome: "Valor Contrato Mín.", ok: regra.valorLiberado >= vcMin, p: formatCurrency(vcMin), c: formatCurrency(regra.valorLiberado) });

  const trocoPositivo = regra.valorTroco > 0;
  const aprovado = trocoPositivo && criterios.every((x) => x.ok);
  return { aprovado, criterios, trocoPositivo };
};

export const computeContratoSim = (contrato, bancos, tabelasComissao, instituicaoSelecionada, idadeCliente) => {
  const inst = instituicaoSelecionada || contrato.instituicao;
  const banco = findBancoDoContrato(bancos, contrato);
  const vp = parseNum(contrato.valor_parcela) || 0;
  const rest = parseInt(contrato.parcelas_restantes) || 0;
  const vtc = vp * rest;
  const pagas = parseInt(contrato.parcela_atual) || 0;
  const build = (tipo) => {
    const isRefin = tipo === "REFINANCIAMENTO";
    const param = banco ? findParametroAtivo(banco, tipo, inst) : null;
    if (isRefin && !param) return { aprovado: false, aprovados: 0, total: 0, rows: [] };
    const idx = param ? (getIndice(param, pagas) || 0) : 0;
    const saldo = vtc - vtc * (idx / 100);
    const regras = getRegrasComissao(tabelasComissao, bancos, banco, inst, tipo, vp, saldo);
    const faixa = param ? getFaixaEtaria(param, idadeCliente) : null;
    const prazoMax = faixa ? (parseNum(faixa.prazoMaximo) ?? null) : null;
    const filt = isRefin ? filtrarRegrasPorPrazo(regras, prazoMax) : agruparPorBanco(regras, prazoMax);
    const rows = filt.map((r) => ({ r, av: avaliarStatus(isRefin ? param : (r.bancoParam || param), contrato, vp, saldo, r, idadeCliente) }));
    return { aprovado: rows.some((x) => x.av.aprovado), aprovados: rows.filter((x) => x.av.aprovado).length, total: rows.length, rows, saldo };
  };
  return { banco, port: build("PORTABILIDADE"), refin: build("REFINANCIAMENTO") };
};

const getSituacaoBadge = (situacao) => {
  const variants = {
    ATIVO: "bg-green-100 text-green-800 border-green-300",
    QUITADO: "bg-blue-100 text-blue-800 border-blue-300",
    PORTABILIDADE: "bg-purple-100 text-purple-800 border-purple-300",
    REFINANCIADO: "bg-orange-100 text-orange-800 border-orange-300",
    SUSPENSO: "bg-yellow-100 text-yellow-800 border-yellow-300",
    CANCELADO: "bg-red-100 text-red-800 border-red-300",
  };
  return (
    <Badge className={cn("border", variants[situacao] || "bg-gray-100 text-gray-800")}>
      {situacao || "N/A"}
    </Badge>
  );
};

// Componente de análise de critérios (REFIN ou PORT)
function AnaliseChecklist({ tipo, analise, restricaoInterna, hideCriterios }) {
  const isRefin = tipo === "REFINANCIAMENTO";
  const labelColor = isRefin ? "text-emerald-600" : "text-blue-600";
  const label = isRefin ? "Refin:" : "Port:";
  
  const restricaoAtiva = isRefin ? restricaoInterna : null;
  const isAprovado = analise?.statusGeral === "APROVADO" && !restricaoAtiva;
  
  return (
    <div className={cn("pt-2", isRefin ? "mt-2 border-t border-slate-200" : "mt-1")}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={cn("text-xs font-semibold uppercase", labelColor)}>
          {label}
        </span>
        
        {/* Badge de status */}
        <div className={cn(
          "flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold text-xs",
          isAprovado ? "bg-green-500 text-white" : "bg-red-500 text-white"
        )}>
          {isAprovado ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" />
              APROVADO
            </>
          ) : (
            <>
              <XCircle className="h-3.5 w-3.5" />
              REPROVADO
            </>
          )}
        </div>
        
        {analise && (
          <span className="text-xs font-medium text-slate-500">
            {analise.aprovados}/{analise.totalCriterios}
          </span>
        )}
        
        {/* Badge de Restrição Interna */}
        {restricaoAtiva && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-600 text-white text-xs font-bold">
            <Ban className="h-3.5 w-3.5" />
            <span>RESTRIÇÃO INTERNA {restricaoAtiva.banco_nome?.split(' - ')[1] || restricaoAtiva.banco_nome}</span>
          </div>
        )}
        
        {/* Critérios detalhados */}
        {!hideCriterios && analise?.criterios?.map((criterio, idx) => (
          <div
            key={idx}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded border text-xs",
              criterio.aprovado
                ? "bg-green-50 border-green-300 text-green-800"
                : "bg-red-50 border-red-300 text-red-800"
            )}
          >
            {criterio.aprovado ? (
              <CheckCircle2 className="h-3 w-3 text-green-600" />
            ) : (
              <XCircle className="h-3 w-3 text-red-600" />
            )}
            <span className="font-semibold">{criterio.nome}</span>
            <span className="text-slate-400">|</span>
            <span>P:{criterio.valorParametro}</span>
            <span className="text-slate-400">|</span>
            <span>C:{criterio.valorContrato}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Componente principal do Card de Contrato
export default function ContratoCard({
  contrato,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  checklistAnalise,
  clienteRestricoesInternas,
  percentualDesconto = "20",
  onAnalisarContrato,
  loadingChecklist,
  bancos = [],
  tabelasComissao = [],
  instituicaoSelecionada = "",
  clienteNascimento = null,
  onAddOcorrencia,
  onDeleteOcorrencia,
}) {
  // Estados locais para Portabilidade/Refin
  const [showPortabilidade, setShowPortabilidade] = useState(false);
  const [showRefin, setShowRefin] = useState(false);
  const [ocorrenciaTexto, setOcorrenciaTexto] = useState("");
  const [salvandoOcorrencia, setSalvandoOcorrencia] = useState(false);
  let _perfilUsuario = "";
  try { _perfilUsuario = String((JSON.parse(localStorage.getItem("usuario") || "{}")).perfil || "").toUpperCase(); } catch (e) {}
  const _podeExcluirOcorrencia = _perfilUsuario !== "ATENDENTE";
  const handleRegistrarOcorrencia = async () => {
    if (!ocorrenciaTexto.trim()) { toast.error("Digite a ocorrencia"); return; }
    setSalvandoOcorrencia(true);
    try {
      await onAddOcorrencia?.(contrato.id, { texto: ocorrenciaTexto.trim() });
      setOcorrenciaTexto("");
    } finally {
      setSalvandoOcorrencia(false);
    }
  };

  // Encontrar restrição interna para este contrato
  const bancosRestritos = clienteRestricoesInternas || [];
  const bancoContrato = contrato.banco_nome || "";
  const restricaoInterna = bancosRestritos.find(r =>
    bancoContrato.includes(r.banco_nome?.split(' - ')[1] || r.banco_nome) ||
    r.banco_nome?.includes(bancoContrato.split(' - ')[0]) ||
    bancoContrato.toLowerCase().includes((r.banco_nome || "").toLowerCase().split(' - ')[1] || "")
  );

  // Obter análises
  const analiseRefin = checklistAnalise?.[`${contrato.id}_REFINANCIAMENTO`];
  const analisePort = checklistAnalise?.[`${contrato.id}_PORTABILIDADE`];

  // Calcular saldo devedor
  const calcularSaldoDevedor = () => {
    const pmt = parseFloat(contrato.valor_parcela) || 0;
    const n = parseInt(contrato.parcelas_restantes) || 0;
    const i = (parseFloat(contrato.taxa_juros) || 0) / 100;

    if (i <= 0) {
      if (contrato.usa_coeficiente && contrato.coeficiente) {
        const coeficiente = parseFloat(contrato.coeficiente);
        return pmt * coeficiente;
      }
      return pmt * n;
    }

    const saldo = pmt * ((1 - Math.pow(1 + i, -n)) / i);
    return saldo;
  };

  const idadeCliente = idadeAnos(clienteNascimento);

  // Resumo de PORTABILIDADE baseado nos bancos de DESTINO (igual a tabela). Restricao interna do banco de ORIGEM NAO impacta a portabilidade.
  const _instPort = instituicaoSelecionada || contrato.instituicao;
  const _bancoOrigemPort = findBancoDoContrato(bancos, contrato);
  const _paramOrigPort = _bancoOrigemPort ? findParametroAtivo(_bancoOrigemPort, "PORTABILIDADE", _instPort) : null;
  const _vpPort = parseNum(contrato.valor_parcela) || 0;
  const _restPort = parseInt(contrato.parcelas_restantes) || 0;
  const _vtcPort = _vpPort * _restPort;
  const _idxPort = _paramOrigPort ? (getIndice(_paramOrigPort, parseInt(contrato.parcela_atual) || 0) || 0) : 0;
  const _saldoPort = _vtcPort - _vtcPort * (_idxPort / 100);
  const _regrasPort = getRegrasComissao(tabelasComissao, bancos, _bancoOrigemPort, _instPort, "PORTABILIDADE", _vpPort, _saldoPort);
  const _faixaPort = _paramOrigPort ? getFaixaEtaria(_paramOrigPort, idadeCliente) : null;
  const _prazoMaxPort = _faixaPort ? (parseNum(_faixaPort.prazoMaximo) ?? null) : null;
  const _portPorBanco = agruparPorBanco(_regrasPort, _prazoMaxPort);
  const _portAvals = _portPorBanco.map((r) => avaliarStatus(r.bancoParam || _paramOrigPort, contrato, _vpPort, _saldoPort, r, idadeCliente));
  const portSummary = _portPorBanco.length > 0 ? {
    statusGeral: _portAvals.some((a) => a.aprovado) ? "APROVADO" : "REPROVADO",
    aprovados: _portAvals.filter((a) => a.aprovado).length,
    totalCriterios: _portPorBanco.length,
  } : null;
  const instituicaoCard = instituicaoSelecionada || contrato.instituicao;
  const saldoRefinCard = calcularSaldoDevedorRefinPort(contrato, bancos, instituicaoCard, "REFINANCIAMENTO");
  const saldoDevedorExibicao = saldoRefinCard != null ? saldoRefinCard : (contrato.saldo_devedor || calcularSaldoDevedor());

  // Handler para toggle Portabilidade
  const handleTogglePortabilidade = async () => {
    const novoEstado = !showPortabilidade;
    setShowPortabilidade(novoEstado);
    setShowRefin(false);
    if (novoEstado && onAnalisarContrato) {
      await onAnalisarContrato(contrato, "PORTABILIDADE");
    }
  };

  // Handler para toggle Refin
  const handleToggleRefin = async () => {
    const novoEstado = !showRefin;
    setShowRefin(novoEstado);
    setShowPortabilidade(false);
    if (novoEstado && onAnalisarContrato) {
      await onAnalisarContrato(contrato, "REFINANCIAMENTO");
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Card Header - Sempre visível */}
      <div
        className={cn(
          "p-4 cursor-pointer hover:bg-slate-50",
          contrato.tipo === "INTERNO" ? "bg-blue-50" : "bg-purple-50"
        )}
        onClick={onToggle}
        data-testid={`contrato-card-${contrato.id}`}
      >
        {/* Linha 1: Tipo e Status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <div className={cn(
              "p-2 rounded-lg",
              contrato.tipo === "INTERNO" ? "bg-blue-100" : "bg-purple-100"
            )}>
              <Landmark className={cn(
                "h-5 w-5",
                contrato.tipo === "INTERNO" ? "text-blue-600" : "text-purple-600"
              )} />
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{contrato.tipo}</Badge>
              {getSituacaoBadge(contrato.situacao)}
              {contrato.tipo_operacao && (
                <Badge variant="secondary" className="text-xs">{contrato.tipo_operacao}</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onEdit && onEdit(contrato); }}
            >
              <Edit className="h-4 w-4 mr-1" />
              Editar
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onDelete && onDelete(contrato.id, contrato.tipo); }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Excluir
            </Button>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Linha 2: Dados do contrato */}
        <div className="grid gap-3 grid-cols-2 md:grid-cols-5 lg:grid-cols-9 pt-2 border-t border-slate-200">
          <div>
            <p className="text-xs text-muted-foreground">Nº Contrato</p>
            <p className="font-mono font-bold text-sm">{contrato.numero_contrato}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Banco</p>
            <p className="font-medium text-sm">{contrato.banco_nome || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Parcela</p>
            <p className="font-semibold text-sm text-green-600">{formatCurrency(contrato.valor_parcela)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Saldo Devedor</p>
            <p className="font-semibold text-sm text-orange-600">
              {formatCurrency(saldoDevedorExibicao)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Pagas/Restantes</p>
            <p className="font-medium text-sm">{contrato.parcela_atual || 0}/{contrato.parcelas_restantes || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Prazo</p>
            <p className="font-medium text-sm">{contrato.quantidade_parcelas || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Taxa de Juros</p>
            <p className="font-medium text-sm">{contrato.taxa_juros ? `${contrato.taxa_juros}%` : "-"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Data Início</p>
            <p className="font-medium text-sm">{formatDate(contrato.data_inicio)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Data Fim</p>
            <p className="font-medium text-sm">{formatDate(contrato.data_fim)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Valor Liberado</p>
            <p className="font-semibold text-sm text-green-600">{contrato.valor_liberado ? formatCurrency(contrato.valor_liberado) : "-"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Data Averbação</p>
            <p className="font-medium text-sm">{contrato.data_averbacao ? formatDate(contrato.data_averbacao) : "-"}</p>
          </div>
          {contrato.usa_coeficiente && (
            <div>
              <p className="text-xs text-muted-foreground">Coeficiente</p>
              <p className="font-medium text-sm">{contrato.coeficiente || "-"}</p>
            </div>
          )}
        </div>

        {/* Linha 3: Observações */}
        {contrato.observacoes && (
          <div className="pt-2 border-t border-slate-200 mt-2">
            <p className="text-xs text-muted-foreground">Observações</p>
            <p className="font-medium text-sm text-blue-600">{contrato.observacoes}</p>
          </div>
        )}

        {/* Linha 4: Checklist de Refinanciamento */}
        {(analiseRefin || restricaoInterna) && (
          <AnaliseChecklist
            tipo="REFINANCIAMENTO"
            analise={analiseRefin}
            restricaoInterna={restricaoInterna}
          />
        )}

        {/* Linha 5: Resumo de Portabilidade — status vem dos bancos de DESTINO; restrição interna do banco de origem NÃO impacta a portabilidade */}
        {portSummary && (
          <AnaliseChecklist
            tipo="PORTABILIDADE"
            analise={portSummary}
            hideCriterios={true}
          />
        )}

      </div>

      {/* Conteúdo expandido */}
      {isExpanded && (
        <div className="border-t bg-white p-4 space-y-4">
          {/* Botões de ação + Checklist ao lado */}
          <div className="flex items-start gap-4 flex-wrap">
            {/* Botões */}
            <div className="flex items-center gap-2">
              <Button 
                variant={showPortabilidade ? "default" : "outline"} 
                size="sm" 
                className="gap-1"
                onClick={(e) => { e.stopPropagation(); handleTogglePortabilidade(); }}
                data-testid={`btn-portabilidade-${contrato.id}`}
              >
                <ArrowRightLeft className="h-4 w-4" />
                Portabilidade
              </Button>
              <Button 
                variant={showRefin ? "default" : "outline"} 
                size="sm" 
                className="gap-1"
                onClick={(e) => { e.stopPropagation(); handleToggleRefin(); }}
                data-testid={`btn-refin-${contrato.id}`}
              >
                <RefreshCw className="h-4 w-4" />
                Refin
              </Button>
            </div>
          </div>

          {/* Tabela de Portabilidade */}
          {showPortabilidade && (
            <TabelaOperacao
              contrato={contrato}
              bancos={bancos}
              tabelasComissao={tabelasComissao}
              tipoOperacao="PORTABILIDADE"
              instituicaoSelecionada={instituicaoSelecionada}
              idadeCliente={idadeCliente}
            />
          )}

          {/* Tabela de Refinanciamento */}
          {showRefin && (
            <TabelaOperacao
              contrato={contrato}
              bancos={bancos}
              tabelasComissao={tabelasComissao}
              tipoOperacao="REFINANCIAMENTO"
              instituicaoSelecionada={instituicaoSelecionada}
              idadeCliente={idadeCliente}
            />
          )}

          {/* Tabela de Descontos para VTC */}
          {contrato.usa_coeficiente && contrato.coeficiente && (
            <div className="mt-4">
              <TabelaDescontos
                contrato={contrato}
                percentualDesconto={percentualDesconto}
              />
            </div>
          )}

          {/* Ocorrencia do atendente */}
          <div className="pt-4 border-t">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="lg:w-1/3 lg:shrink-0">
                <p className="text-sm font-semibold text-slate-700 mb-2">Ocorrencia do atendente</p>
                <Textarea
                  value={ocorrenciaTexto}
                  onChange={(e) => setOcorrenciaTexto(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Descreva a ocorrencia do atendimento..."
                  className="min-h-[80px] text-sm"
                  data-testid={`ocorrencia-texto-${contrato.id}`}
                />
                <div className="flex justify-end mt-2">
                  <Button type="button" size="sm" disabled={salvandoOcorrencia || !ocorrenciaTexto.trim()}
                    onClick={(e) => { e.stopPropagation(); handleRegistrarOcorrencia(); }}
                    data-testid={`ocorrencia-registrar-${contrato.id}`}>
                    {salvandoOcorrencia ? "Registrando..." : "Registrar ocorrencia"}
                  </Button>
                </div>
              </div>
              {(contrato.ocorrencias || []).length > 0 && (
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 content-start max-h-[260px] overflow-y-auto">
                  {[...(contrato.ocorrencias || [])].reverse().map((oc) => (
                    <div key={oc.id || oc.data_hora} className="relative rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs h-fit" data-testid={`ocorrencia-item-${contrato.id}`}>
                      {_podeExcluirOcorrencia && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); onDeleteOcorrencia && onDeleteOcorrencia(contrato.id, oc.id); }}
                          className="absolute top-1 right-1 text-red-400 hover:text-red-600 p-0.5"
                          title="Excluir ocorrencia" data-testid={`ocorrencia-excluir-${oc.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <div className="flex items-center gap-2 text-slate-500 mb-1 flex-wrap pr-5">
                        <span className="font-medium text-slate-700">{oc.operador || "—"}</span>
                        <span>•</span>
                        <span>{oc.data_hora ? new Date(oc.data_hora).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "-"}</span>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap break-words">{oc.texto}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Tabela unificada REFINANCIAMENTO / PORTABILIDADE (parâmetros dinâmicos) =====
function TabelaOperacao({ contrato, bancos, tabelasComissao, tipoOperacao, instituicaoSelecionada, idadeCliente }) {
  const isRefin = tipoOperacao === "REFINANCIAMENTO";
  const Icon = isRefin ? RefreshCw : ArrowRightLeft;
  const headerColor = isRefin ? "bg-emerald-800" : "bg-slate-800";
  const rowHeadColor = isRefin ? "bg-emerald-700" : "bg-slate-700";

  const instituicao = instituicaoSelecionada || contrato.instituicao;
  const banco = findBancoDoContrato(bancos, contrato);
  const param = banco ? findParametroAtivo(banco, tipoOperacao, instituicao) : null;

  // Sem parâmetro ativo -> oculta só no REFIN; PORTABILIDADE segue (usa bancos de destino)
  if (!param && isRefin) {
    return (
      <div
        className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-center gap-3"
        data-testid={`tabela-sem-parametro-${tipoOperacao.toLowerCase()}`}
      >
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
        <div>
          <p className="font-semibold text-amber-800 text-sm">
            Sem tabela de {isRefin ? "Refinanciamento" : "Portabilidade"} cadastrada
          </p>
          <p className="text-xs text-amber-700">
            Não há parâmetro ATIVO para {contrato.banco_nome || "este banco"} / {instituicao || "esta instituição"}.
          </p>
        </div>
      </div>
    );
  }

  const valorParcela = parseNum(contrato.valor_parcela) || 0;
  const parcelasRestantes = parseInt(contrato.parcelas_restantes) || 0;
  const parcelasPagas = parseInt(contrato.parcela_atual) || 0;
  const vtc = valorParcela * parcelasRestantes;
  const indice = param ? (getIndice(param, parcelasPagas) || 0) : 0;
  const desconto = vtc * (indice / 100);
  const saldoDevedor = vtc - desconto;

  const regras = getRegrasComissao(tabelasComissao, bancos, banco, instituicao, tipoOperacao, valorParcela, saldoDevedor);

  // Limite de idade -> prazo máximo permitido; mantém só o maior prazo por produto
  const faixa = param ? getFaixaEtaria(param, idadeCliente) : null;
  const prazoMaximo = faixa ? (parseNum(faixa.prazoMaximo) ?? null) : null;
  const limiteMaximo = faixa ? parseNum(faixa.limiteMaximo) : null;
  const regrasFiltradas = isRefin ? filtrarRegrasPorPrazo(regras, prazoMaximo) : agruparPorBanco(regras, prazoMaximo);
  const trocoMinParam = param ? parseNum(param.valorTroco) : null;
  let _usuarioPerfil = "";
  try { _usuarioPerfil = String((JSON.parse(localStorage.getItem("usuario") || "{}")).perfil || "").toUpperCase(); } catch (e) {}
  const isAtendente = _usuarioPerfil === "ATENDENTE";

  const regrasComAv = regrasFiltradas.map((r) => {
    const paramCrit = isRefin ? param : (r.bancoParam || param);
    const av = avaliarStatus(paramCrit, contrato, valorParcela, saldoDevedor, r, idadeCliente);
    return { r, av, paramCrit };
  });
  regrasComAv.sort((a, b) => (b.av.aprovado ? 1 : 0) - (a.av.aprovado ? 1 : 0) || ((b.r.valorTroco || 0) - (a.r.valorTroco || 0)));

  const handleCopiarSimulacao = () => {
    const aprovadas = regrasComAv.filter((x) => x.av.aprovado);
    if (!aprovadas.length) { toast.error("Nenhuma simulação aprovada para copiar."); return; }
    const linhas = aprovadas.map(({ r }) => {
      const nome = isRefin ? (r.produto || "-") : r.bancoNome;
      const sub = isRefin ? "" : (r.produto ? `\n   _${r.produto}_` : "");
      return `\n✅ *${nome}*${sub}\n   • Prazo: ${r.prazo || "-"}x\n   • Valor bruto: ${formatCurrency(r.valorLiberado)}\n   • Valor liberado: ${formatCurrency(r.valorTroco)}`;
    }).join("\n");
    const origem = banco?.nome || contrato.banco_nome || "Banco origem";
    const txt = `*SIMULAÇÃO DE ${isRefin ? "REFINANCIAMENTO" : "PORTABILIDADE"}*\n` +
      (contrato.cliente_nome ? `Cliente: ${contrato.cliente_nome}\n` : "") +
      `Contrato: ${contrato.numero_contrato || "-"} (${origem})\n` +
      `Instituição: ${instituicao || "-"}\n` +
      `Parcela atual: ${formatCurrency(valorParcela)}\n` +
      `Saldo devedor: ${formatCurrency(saldoDevedor)}\n` +
      linhas;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(txt).then(() => toast.success("Simulação copiada para o WhatsApp!")).catch(() => toast.error("Falha ao copiar."));
    } else {
      toast.error("Cópia não suportada neste navegador.");
    }
  };

  return (
    <div className="mt-4 rounded-lg overflow-hidden border" data-testid={`tabela-${tipoOperacao.toLowerCase()}`}>
      <div className={cn(headerColor, "text-white p-3 flex items-center justify-between flex-wrap gap-2")}>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          <span className="font-bold text-lg">{isRefin ? "REFINANCIAMENTO" : "PORTABILIDADE"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs opacity-80">{banco?.nome || contrato.banco_nome || "Banco origem"} · {instituicao || "-"}</span>
          <Button
            size="sm"
            variant="secondary"
            className="h-7 gap-1 text-xs"
            onClick={(e) => { e.stopPropagation(); handleCopiarSimulacao(); }}
            data-testid={`btn-copiar-simulacao-${tipoOperacao.toLowerCase()}`}
          >
            <Copy className="h-3.5 w-3.5" /> Copiar Simulação
          </Button>
        </div>
      </div>

      {/* Bloco de Saldo Devedor (única base de cálculo) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 bg-slate-50 border-b text-sm">
        <div>
          <p className="text-xs text-muted-foreground">VTC (parc × rest.)</p>
          <p className="font-semibold">{formatCurrency(vtc)}</p>
          <p className="text-[10px] text-slate-400">{formatCurrency(valorParcela)} × {parcelasRestantes}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Índice (pagas: {parcelasPagas})</p>
          <p className="font-semibold text-blue-600">{indice}%</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Desconto</p>
          <p className="font-semibold text-red-600">- {formatCurrency(desconto)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Saldo Devedor</p>
          <p className="font-bold text-orange-600">{formatCurrency(saldoDevedor)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Tabelas</p>
          <p className="font-semibold">{regrasFiltradas.length}</p>
        </div>
      </div>

      {/* Regra de idade / prazo permitido */}
      {idadeCliente != null && (
        <div className="px-3 py-1.5 bg-blue-50 border-b text-xs text-blue-800 flex items-center gap-2 flex-wrap" data-testid={`regra-idade-${tipoOperacao.toLowerCase()}`}>
          <span className="font-semibold">Idade: {idadeCliente} anos</span>
          {faixa ? (
            <>
              <span>· Faixa {faixa.idadeInicial}–{faixa.idadeFinal}</span>
              <span>· Prazo máx. permitido: <strong>{prazoMaximo ?? "-"}</strong></span>
              {limiteMaximo != null && <span>· Limite CPF: <strong>{formatCurrency(limiteMaximo)}</strong></span>}
            </>
          ) : (
            <span className="text-amber-700">Sem faixa etária cadastrada para esta idade</span>
          )}
        </div>
      )}

      {regrasFiltradas.length === 0 ? (
        <div className="p-4 flex items-center gap-2 text-amber-700 bg-amber-50 text-sm" data-testid={`tabela-sem-regras-${tipoOperacao.toLowerCase()}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {isRefin ? `Nenhuma tabela de comissão dentro do limite de idade/prazo para ${banco.nome} / ${instituicao || "-"} (REFIN).` : `Nenhum outro banco com tabela de PORTABILIDADE habilitada para ${instituicao || "esta instituição"} (a portabilidade é de um banco para outro).`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={cn(rowHeadColor, "text-white")}>
                <th className="px-3 py-2 text-left font-semibold">PRODUTO / TABELA</th>
                <th className="px-3 py-2 text-center font-semibold">PRAZO</th>
                <th className="px-3 py-2 text-center font-semibold">FATOR</th>
                <th className="px-3 py-2 text-center font-semibold">TAXA</th>
                <th className="px-3 py-2 text-right font-semibold">VALOR BRUTO</th>
                <th className="px-3 py-2 text-right font-semibold">SALDO DEVEDOR</th>
                <th className="px-3 py-2 text-right font-semibold">VALOR LIBERADO</th>
                <th className="px-3 py-2 text-right font-semibold">
                  <div>COMISSÃO</div>
                  {!isAtendente && <div className="text-[10px] opacity-80">EMP % · ATD %</div>}
                </th>
                <th className="px-3 py-2 text-center font-semibold">STATUS</th>
                <th className="px-3 py-2 text-left font-semibold">JANELA DE OPORTUNIDADE</th>
              </tr>
            </thead>
            <tbody>
              {regrasComAv.map(({ r, av, paramCrit }, idx) => {
                const reprovados = av.criterios.filter((c) => !c.ok).map((c) => c.nome);
                if (!av.trocoPositivo) reprovados.push("Troco negativo");
                const _trocoMinRow = isRefin ? trocoMinParam : ((paramCrit ? parseNum(paramCrit.valorTroco) : null) ?? trocoMinParam);
                return (
                  <Fragment key={idx}>
                  <tr className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"} data-testid={`regra-${tipoOperacao.toLowerCase()}-${idx}`}>
                    <td className="px-3 py-2 font-medium">
                      {isRefin ? r.produto : (
                        <div>
                          <div className="font-semibold">{r.bancoNome}</div>
                          <div className="text-[10px] text-slate-400">{r.produto}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{r.prazo || "-"}</td>
                    <td className="px-3 py-2 text-center font-mono">{r.fator.toFixed(5)}</td>
                    <td className="px-3 py-2 text-center">{r.taxa ? `${r.taxa}%` : "-"}</td>
                    <td className="px-3 py-2 text-right font-bold text-blue-600">{formatCurrency(r.valorLiberado)}</td>
                    <td className="px-3 py-2 text-right text-orange-600">{formatCurrency(saldoDevedor)}</td>
                    <td className={cn("px-3 py-2 text-right font-bold", r.valorTroco >= 0 ? "text-emerald-600" : "text-red-600")}>
                      {formatCurrency(r.valorTroco)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-semibold text-green-700">{formatCurrency(r.comissao)}{!isAtendente && <span className="text-[10px] text-slate-400 font-normal"> EMP {r.empresaPct}%</span>}</div>
                      <div className="font-semibold text-teal-700">{formatCurrency(r.comissaoAtendente)}{!isAtendente && <span className="text-[10px] text-slate-400 font-normal"> ATD {r.atendentePct}%</span>}</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div
                        className={cn(
                          "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold",
                          av.aprovado ? "bg-green-500 text-white" : "bg-red-500 text-white"
                        )}
                        title={reprovados.length ? `Reprovado: ${reprovados.join(", ")}` : "Todos critérios aprovados"}
                      >
                        {av.aprovado ? (
                          <><CheckCircle2 className="h-3 w-3" /> APROVADO</>
                        ) : (
                          <><XCircle className="h-3 w-3" /> REPROVADO</>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-left text-xs" data-testid={`janela-${tipoOperacao.toLowerCase()}-${idx}`}>
                      {(() => {
                        const j = projetarJanela(r.valorLiberado, contrato, param, _trocoMinRow);
                        if (!j) return <span className="text-slate-400">Sem janela no prazo</span>;
                        if (j.agora) return <span className="text-emerald-600 font-semibold">Elegível agora · {formatCurrency(j.valor)}</span>;
                        return (
                          <div className="space-y-0.5">
                            <div><span className="text-slate-500">Data prevista:</span> <strong>{j.data.toLocaleDateString("pt-BR")}</strong></div>
                            <div><span className="text-slate-500">Valor estimado:</span> <strong className="text-emerald-600">{formatCurrency(j.valor)}</strong></div>
                            <div className="text-[10px] text-slate-400">Negativo hoje, elegível em ~{j.meses} {j.meses === 1 ? "mês" : "meses"}</div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                  {!isRefin && av.criterios.length > 0 && (
                    <tr className="bg-slate-50/60" data-testid={`criterios-${tipoOperacao.toLowerCase()}-${idx}`}>
                      <td colSpan={10} className="px-3 py-1.5 border-b border-slate-200">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase">Critérios {r.bancoNome}:</span>
                          {av.criterios.map((cri, ci) => (
                            <span key={ci} className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px]", cri.ok ? "bg-green-50 border-green-300 text-green-800" : "bg-red-50 border-red-300 text-red-800")}>
                              {cri.ok ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                              <span className="font-semibold">{cri.nome}</span>
                              <span className="text-slate-400">P:{cri.p}</span>
                              <span className="text-slate-400">C:{cri.c}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Componente de tabela de descontos (para contratos com coeficiente)
function TabelaDescontos({ contrato, percentualDesconto }) {
  const pmt = parseFloat(contrato.valor_parcela) || 0;
  const coeficiente = parseFloat(contrato.coeficiente) || 0;
  const vtc = pmt * coeficiente;
  const parcelasPagas = parseInt(contrato.parcela_atual) || 0;
  const totalParcelas = parseInt(contrato.quantidade_parcelas) || 96;

  const faixas = [
    { label: "1 a 10", min: 1, max: 10 },
    { label: "11 a 20", min: 11, max: 20 },
    { label: "21 a 30", min: 21, max: 30 },
    { label: "31 a 40", min: 31, max: 40 },
    { label: "41 a 60", min: 41, max: 60 },
    { label: "61 a 70", min: 61, max: 70 },
    { label: "71 a 100", min: 71, max: 100 },
  ];

  const faixaAtual = faixas.find(f => parcelasPagas >= f.min && parcelasPagas <= f.max);
  if (!faixaAtual) return null;

  const restantes = totalParcelas - faixaAtual.max;
  const saldoInicial = vtc * (restantes / totalParcelas);
  const desc10 = saldoInicial * 0.90;
  const desc20 = saldoInicial * 0.80;
  const desc30 = saldoInicial * 0.70;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-blue-100">
            <th className="px-2 py-2 text-left font-semibold border">Faixa Atual</th>
            <th className="px-2 py-2 text-center font-semibold border">Parc. Restantes</th>
            <th className="px-2 py-2 text-right font-semibold border">Saldo Inicial</th>
            <th className={cn("px-2 py-2 text-right font-semibold border", percentualDesconto === "10" && "bg-green-200")}>10% Desconto</th>
            <th className={cn("px-2 py-2 text-right font-semibold border", percentualDesconto === "20" && "bg-green-200")}>20% Desconto</th>
            <th className={cn("px-2 py-2 text-right font-semibold border", percentualDesconto === "30" && "bg-green-200")}>30% Desconto</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-yellow-100 font-bold">
            <td className="px-2 py-2 border text-yellow-800">{faixaAtual.label} parc.</td>
            <td className="px-2 py-2 border text-center">{restantes}</td>
            <td className="px-2 py-2 border text-right text-gray-600">
              R$ {saldoInicial.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </td>
            <td className={cn("px-2 py-2 border text-right", percentualDesconto === "10" ? "bg-green-300 text-green-900" : "text-green-700")}>
              R$ {desc10.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </td>
            <td className={cn("px-2 py-2 border text-right", percentualDesconto === "20" ? "bg-green-300 text-green-900" : "text-green-700")}>
              R$ {desc20.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </td>
            <td className={cn("px-2 py-2 border text-right", percentualDesconto === "30" ? "bg-green-300 text-green-900" : "text-green-700")}>
              R$ {desc30.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-blue-600 mt-2">
        * VTC = R$ {vtc.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | Parcelas Pagas: {parcelasPagas} | <strong>Desconto Selecionado: {percentualDesconto}%</strong>
      </p>
    </div>
  );
}

export { AnaliseChecklist, TabelaDescontos };
